import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import {
  Plus, Trash, Brain, Sparkle, MapPin, Warning, Megaphone, ChatCircleDots, FloppyDisk,
  ArrowRight, ArrowLeft, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { Brain, Sparkle, MapPin, Warning, Megaphone, ChatCircleDots, FloppyDisk };

const WHEN_PRESETS = [
  { mode: "once", label: "Una sola vez", helper: "En una fecha y hora específica" },
  { mode: "every", label: "Cada cierto tiempo", helper: "Cada N minutos / horas / días" },
  { mode: "daily", label: "Todos los días", helper: "A una hora fija (Guatemala)" },
  { mode: "weekly", label: "Días específicos", helper: "Lun-Vie a las 9am, por ejemplo" },
  { mode: "monthly", label: "Mensualmente", helper: "El día N de cada mes" },
];

const DAYS = [
  { key: "mon", label: "Lun" }, { key: "tue", label: "Mar" }, { key: "wed", label: "Mié" },
  { key: "thu", label: "Jue" }, { key: "fri", label: "Vie" }, { key: "sat", label: "Sáb" }, { key: "sun", label: "Dom" },
];

export default function Scheduler() {
  const { data, loading, reload } = useFetch(() => api.get("/scheduler/jobs").then((r) => r.data), []);
  const [showCreate, setShowCreate] = useState(false);

  const remove = async (id) => {
    if (!confirm("¿Eliminar este job?")) return;
    try { await api.delete(`/scheduler/jobs/${id}`); toast.success("Job eliminado"); reload(); }
    catch { toast.error("Error"); }
  };

  return (
    <>
      <PageHeader
        label="Engine"
        title="Scheduler"
        description="Automatizaciones programadas: ciclo IA, highlights colectivos, campañas, check-ins, backups."
      >
        <button onClick={() => setShowCreate(true)} data-testid="create-job-btn" className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover">
          <Plus size={16} /> Nueva automatización
        </button>
      </PageHeader>
      <PageBody>
        {loading && <div className="text-carbon-500">Cargando...</div>}
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-carbon-200 text-left">
                <th className="px-5 py-3 section-label">Nombre</th>
                <th className="px-5 py-3 section-label">Qué hace</th>
                <th className="px-5 py-3 section-label">Cuándo</th>
                <th className="px-5 py-3 section-label">Estado</th>
                <th className="px-5 py-3 section-label">Último run</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && (data || []).length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-carbon-500">Aún sin automatizaciones programadas.</td></tr>
              )}
              {(data || []).map((j) => (
                <tr key={j.id} className="border-b border-carbon-100" data-testid={`job-row-${j.id}`}>
                  <td className="px-5 py-3 font-medium">{j.name}</td>
                  <td className="px-5 py-3"><Badge tone="terracotta">{labelForJobType(j.job_type)}</Badge></td>
                  <td className="px-5 py-3 text-xs text-carbon-700">{j.payload?._human_summary || `${j.schedule_type}`}</td>
                  <td className="px-5 py-3"><Badge tone={j.status === "completed" ? "forest" : j.status === "failed" ? "signal" : "neutral"}>{j.status}</Badge></td>
                  <td className="px-5 py-3 font-mono text-xs text-carbon-500">{j.last_run_at ? new Date(j.last_run_at).toLocaleString() : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => remove(j.id)} className="text-carbon-500 hover:text-signal p-1" data-testid={`delete-job-${j.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </PageBody>
      {showCreate && <FriendlyWizard onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); toast.success("Automatización programada"); }} />}
    </>
  );
}

function labelForJobType(jt) {
  const m = {
    intelligence_daily_cycle: "Ciclo IA completo",
    intelligence_highlights: "Resumen colectivo",
    intelligence_territory_recompute: "Recalcular territorios",
    ambassador_inactivity_check: "Detectar inactivos",
    run_campaign: "Lanzar campaña",
    send_message: "Enviar mensaje",
    admin_backup: "Backup BD",
  };
  return m[jt] || jt;
}

function FriendlyWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [action, setAction] = useState(null);
  const [actionParams, setActionParams] = useState({});
  const [when, setWhen] = useState({ mode: "daily", hour: 9, minute: 0, timezone_name: "America/Guatemala" });
  const [busy, setBusy] = useState(false);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    api.get("/scheduler/actions").then((r) => setActions(r.data.actions || []));
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/scheduler/jobs/friendly", {
        name: name || `${action.label} ${new Date().toLocaleDateString()}`,
        action: action.key,
        action_params: actionParams,
        when,
      });
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto scrollbar-thin">
        <div className="flex items-center justify-between mb-1">
          <div className="section-label">Asistente · paso {step} de 3</div>
          <Stepper step={step} />
        </div>
        <h2 className="font-heading text-2xl mb-1">Nueva automatización</h2>
        <p className="text-xs text-carbon-500 mb-5">Decinos QUÉ hacer y CUÁNDO. Sin código, sin JSON.</p>

        {step === 1 && (
          <Step1ChooseAction actions={actions} selected={action} onPick={(a) => { setAction(a); setStep(2); }} />
        )}
        {step === 2 && action && (
          <Step2Params action={action} values={actionParams} onChange={setActionParams} />
        )}
        {step === 3 && (
          <Step3When when={when} setWhen={setWhen} name={name} setName={setName} action={action} />
        )}

        <div className="flex justify-between gap-2 pt-6 border-t border-carbon-200 mt-6">
          <div>
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} data-testid="wizard-back" className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded flex items-center gap-2">
                <ArrowLeft size={14} /> Atrás
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} disabled={step === 1 && !action} data-testid="wizard-next" className="px-4 py-2 text-sm bg-carbon-900 text-white rounded hover:bg-carbon-800 disabled:opacity-50 flex items-center gap-2">
                Siguiente <ArrowRight size={14} />
              </button>
            ) : (
              <button onClick={submit} disabled={busy} data-testid="wizard-submit" className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-60 flex items-center gap-2">
                <CheckCircle size={14} /> {busy ? "Programando..." : "Programar"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3].map((n) => (
        <div key={n} className={`w-6 h-1 rounded ${step >= n ? "bg-terracotta" : "bg-carbon-200"}`} />
      ))}
    </div>
  );
}

function Step1ChooseAction({ actions, selected, onPick }) {
  return (
    <div>
      <h3 className="font-medium text-base mb-3">¿Qué querés que haga?</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {actions.map((a) => {
          const Icon = ICONS[a.icon] || Brain;
          const active = selected?.key === a.key;
          return (
            <button
              key={a.key}
              onClick={() => onPick(a)}
              data-testid={`action-${a.key}`}
              className={`text-left border rounded p-3 hover:border-terracotta transition-colors ${active ? "border-terracotta bg-terracotta/5" : "border-carbon-200"}`}
            >
              <div className="flex items-start gap-3">
                <Icon size={18} className="text-terracotta mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-sm">{a.label}</div>
                  <div className="text-xs text-carbon-500 mt-0.5 leading-relaxed">{a.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Step2Params({ action, values, onChange }) {
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const needsUsers = action.params.some((p) => p.type === "user_select");
  const needsCampaigns = action.params.some((p) => p.type === "campaign_select");
  const needsPipelines = action.params.some((p) => p.type === "pipeline_select");

  useEffect(() => {
    if (needsUsers) api.get("/users?limit=500").then((r) => setUsers(r.data?.items || []));
    if (needsCampaigns) api.get("/campaigns").then((r) => setCampaigns(r.data || []));
    if (needsPipelines) api.get("/pipelines").then((r) => setPipelines(r.data || []));
  }, []); // eslint-disable-line

  const update = (k, v) => onChange({ ...values, [k]: v });

  if (action.params.length === 0) {
    return (
      <div>
        <h3 className="font-medium text-base mb-3">Configuración</h3>
        <div className="border border-forest/30 bg-forest/5 rounded p-4 text-sm text-carbon-700">
          <CheckCircle size={16} className="inline text-forest mr-2" />
          Esta acción no necesita configuración extra. Pasá a "Siguiente" para definir cuándo se ejecuta.
        </div>
      </div>
    );
  }
  return (
    <div>
      <h3 className="font-medium text-base mb-3">Configuración de la acción</h3>
      <div className="space-y-3">
        {action.params.map((p) => (
          <div key={p.key}>
            <label className="section-label block mb-1">
              {p.label} {p.required && <span className="text-signal">*</span>}
            </label>
            {p.type === "number" && (
              <input type="number" min={p.min} max={p.max} value={values[p.key] ?? p.default ?? ""}
                onChange={(e) => update(p.key, parseInt(e.target.value, 10))}
                data-testid={`param-${p.key}`}
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
            )}
            {p.type === "text" && (
              <input value={values[p.key] ?? p.default ?? ""}
                onChange={(e) => update(p.key, e.target.value)}
                data-testid={`param-${p.key}`}
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
            )}
            {p.type === "textarea" && (
              <textarea rows={3} value={values[p.key] ?? p.default ?? ""}
                onChange={(e) => update(p.key, e.target.value)}
                data-testid={`param-${p.key}`}
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
            )}
            {p.type === "user_select" && (
              <select value={values[p.key] ?? ""} onChange={(e) => update(p.key, e.target.value)}
                data-testid={`param-${p.key}`}
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="">— Elegir usuario —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} · {u.public_id}</option>
                ))}
              </select>
            )}
            {p.type === "campaign_select" && (
              <select value={values[p.key] ?? ""} onChange={(e) => update(p.key, e.target.value)}
                data-testid={`param-${p.key}`}
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="">— Elegir campaña —</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            {p.type === "pipeline_select" && (
              <select value={values[p.key] ?? ""} onChange={(e) => update(p.key, e.target.value)}
                data-testid={`param-${p.key}`}
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="">Todos los pipelines</option>
                {pipelines.map((pl) => (
                  <option key={pl.id} value={pl.slug}>{pl.name}</option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Step3When({ when, setWhen, name, setName, action }) {
  const summary = useMemo(() => describeWhen(when), [when]);
  const setMode = (mode) => {
    const base = { mode, timezone_name: "America/Guatemala", minute: 0 };
    if (mode === "daily") setWhen({ ...base, hour: 9 });
    else if (mode === "weekly") setWhen({ ...base, hour: 9, days_of_week: ["mon"] });
    else if (mode === "monthly") setWhen({ ...base, hour: 9, day_of_month: 1 });
    else if (mode === "every") setWhen({ ...base, every_n: 1, every_unit: "hours" });
    else if (mode === "once") setWhen({ ...base, at_iso: defaultAtIso() });
  };

  return (
    <div>
      <label className="section-label block mb-1">Nombre de la automatización</label>
      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="wizard-name"
        placeholder={`${action?.label || "Automatización"} ${new Date().toLocaleDateString()}`}
        className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm mb-5" />

      <h3 className="font-medium text-base mb-3">¿Cuándo se ejecuta?</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {WHEN_PRESETS.map((p) => (
          <button key={p.mode} onClick={() => setMode(p.mode)}
            data-testid={`when-${p.mode}`}
            className={`text-left border rounded p-2.5 hover:border-terracotta transition-colors ${when.mode === p.mode ? "border-terracotta bg-terracotta/5" : "border-carbon-200"}`}>
            <div className="font-medium text-xs">{p.label}</div>
            <div className="text-[10px] text-carbon-500 mt-0.5">{p.helper}</div>
          </button>
        ))}
      </div>

      <div className="border border-carbon-200 rounded p-4 mb-4">
        {when.mode === "once" && (
          <label className="block">
            <span className="section-label">Fecha y hora exacta</span>
            <input type="datetime-local" value={localFromIso(when.at_iso)}
              onChange={(e) => setWhen({ ...when, at_iso: isoFromLocal(e.target.value) })}
              data-testid="when-at-iso"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
          </label>
        )}
        {when.mode === "every" && (
          <div className="flex items-end gap-3">
            <label className="block flex-1">
              <span className="section-label">Cada</span>
              <input type="number" min={1} value={when.every_n || 1}
                onChange={(e) => setWhen({ ...when, every_n: parseInt(e.target.value, 10) })}
                data-testid="when-every-n"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
            </label>
            <label className="block flex-1">
              <span className="section-label">Unidad</span>
              <select value={when.every_unit || "hours"} onChange={(e) => setWhen({ ...when, every_unit: e.target.value })}
                data-testid="when-every-unit"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="minutes">minutos</option>
                <option value="hours">horas</option>
                <option value="days">días</option>
              </select>
            </label>
          </div>
        )}
        {when.mode === "daily" && (
          <HourMinutePicker when={when} setWhen={setWhen} />
        )}
        {when.mode === "weekly" && (
          <>
            <div className="section-label mb-2">Días de la semana</div>
            <div className="flex flex-wrap gap-1 mb-3">
              {DAYS.map((d) => {
                const active = (when.days_of_week || []).includes(d.key);
                return (
                  <button key={d.key}
                    onClick={() => {
                      const cur = when.days_of_week || [];
                      const next = active ? cur.filter((x) => x !== d.key) : [...cur, d.key];
                      setWhen({ ...when, days_of_week: next });
                    }}
                    data-testid={`when-day-${d.key}`}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${active ? "bg-terracotta text-white border-terracotta" : "border-carbon-200 hover:border-terracotta"}`}>
                    {d.label}
                  </button>
                );
              })}
            </div>
            <HourMinutePicker when={when} setWhen={setWhen} />
          </>
        )}
        {when.mode === "monthly" && (
          <div className="flex items-end gap-3">
            <label className="block">
              <span className="section-label">Día del mes</span>
              <input type="number" min={1} max={28} value={when.day_of_month || 1}
                onChange={(e) => setWhen({ ...when, day_of_month: parseInt(e.target.value, 10) })}
                data-testid="when-day-of-month"
                className="mt-1 w-24 bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
            </label>
            <HourMinutePicker when={when} setWhen={setWhen} />
          </div>
        )}
      </div>

      <div className="border border-forest/30 bg-forest/5 rounded p-3 text-sm">
        <CheckCircle size={14} className="inline text-forest mr-2" />
        <strong>Resumen:</strong> {summary}
      </div>
    </div>
  );
}

function HourMinutePicker({ when, setWhen }) {
  return (
    <div className="flex items-end gap-3">
      <label className="block">
        <span className="section-label">Hora (Guatemala)</span>
        <input type="number" min={0} max={23} value={when.hour ?? 9}
          onChange={(e) => setWhen({ ...when, hour: parseInt(e.target.value, 10) })}
          data-testid="when-hour"
          className="mt-1 w-24 bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
      </label>
      <span className="pb-2 text-xl font-mono">:</span>
      <label className="block">
        <span className="section-label">Minuto</span>
        <input type="number" min={0} max={59} value={when.minute ?? 0}
          onChange={(e) => setWhen({ ...when, minute: parseInt(e.target.value, 10) })}
          data-testid="when-minute"
          className="mt-1 w-24 bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
      </label>
    </div>
  );
}

function describeWhen(w) {
  if (!w) return "—";
  if (w.mode === "once") return `Una sola vez · ${w.at_iso ? new Date(w.at_iso).toLocaleString() : "(elegí fecha)"}`;
  if (w.mode === "every") return `Cada ${w.every_n || "?"} ${w.every_unit || "?"}`;
  const t = `${String(w.hour ?? 9).padStart(2, "0")}:${String(w.minute ?? 0).padStart(2, "0")} hora Guatemala`;
  if (w.mode === "daily") return `Todos los días a las ${t}`;
  if (w.mode === "weekly") return `${(w.days_of_week || []).map((d) => DAYS.find((x) => x.key === d)?.label || d).join(", ") || "(elegí días)"} a las ${t}`;
  if (w.mode === "monthly") return `Día ${w.day_of_month || "?"} de cada mes a las ${t}`;
  return "—";
}

function defaultAtIso() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setSeconds(0, 0);
  return d.toISOString();
}
function localFromIso(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60000).toISOString().slice(0, 16);
}
function isoFromLocal(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}
